import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Check, ExternalLink, Loader2, X } from 'lucide-react';
import api, { errMsg, fileUrl } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const TABS = [
  { id: 'pending', label: 'Bekleyen' },
  { id: 'approved', label: 'Onaylanan' },
  { id: 'rejected', label: 'Reddedilen' },
  { id: 'all', label: 'Tümü' },
];

const STATUS = {
  pending: { label: 'Bekliyor', cls: 'bg-[hsl(38,92%,50%)] text-black' },
  approved: { label: 'Onaylandı', cls: 'bg-[hsl(142,70%,45%)] text-black' },
  rejected: { label: 'Reddedildi', cls: 'bg-[hsl(var(--brand-red))] text-white' },
};

export const AdminPayments = ({ onChanged }) => {
  const [tab, setTab] = useState('pending');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(null);
  const [mode, setMode] = useState('approve');
  const [credits, setCredits] = useState(0);
  const [adminNote, setAdminNote] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/payment-requests', {
        params: tab === 'all' ? {} : { status: tab },
      });
      setItems(data.requests);
    } catch (e) {
      toast.error(errMsg(e, 'Ödeme bildirimleri yüklenemedi'));
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  const decide = async () => {
    if (!target) return;
    setSaving(true);
    try {
      const url = `/admin/payment-requests/${target.id}/${mode}`;
      await api.post(url, {
        credits: mode === 'approve' ? Number(credits) : null,
        admin_note: adminNote,
      });
      toast.success(mode === 'approve' ? 'Onaylandı, hak yüklendi' : 'Bildirim reddedildi');
      setTarget(null);
      setAdminNote('');
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(errMsg(e, 'İşlem yapılamadı'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div
        data-testid="admin-payments-tabs"
        className="flex w-fit items-center gap-1 rounded-[12px] border border-[hsl(var(--border-soft))] bg-[hsl(var(--surface))] p-1"
      >
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            data-testid={`admin-payments-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={[
              'rounded-[9px] px-3 py-1.5 text-xs font-semibold transition-colors duration-150',
              tab === t.id
                ? 'bg-[hsl(var(--brand-red))] text-white'
                : 'text-[hsl(var(--text-muted))] hover:text-white',
            ].join(' ')}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid place-items-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--brand-red))]" />
        </div>
      ) : items.length === 0 ? (
        <p data-testid="empty-state" className="panel px-5 py-10 text-center text-sm text-[hsl(var(--text-muted))]">
          Bu durumda ödeme bildirimi yok.
        </p>
      ) : (
        <div data-testid="admin-payment-requests-list" className="grid gap-3 lg:grid-cols-2">
          {items.map((r) => {
            const st = STATUS[r.status] || STATUS.pending;
            return (
              <div key={r.id} data-testid={`admin-payment-${r.id}`} className="panel p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-display text-sm font-semibold">{r.user_name || r.user_email}</p>
                    <p className="truncate text-xs text-[hsl(var(--text-muted))]">{r.user_email}</p>
                  </div>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${st.cls}`}>
                    {st.label}
                  </span>
                </div>

                <dl className="mt-3 grid gap-1.5 text-xs">
                  <div className="flex justify-between gap-3">
                    <dt className="text-[hsl(var(--text-muted))]">Paket</dt>
                    <dd className="font-medium">
                      {r.package_label} - {r.credits} hak{r.amount ? ` / ${r.amount} TL` : ''}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-[hsl(var(--text-muted))]">Yöntem</dt>
                    <dd className="font-medium">
                      {r.method === 'bank' ? 'Havale / IBAN' : r.method === 'link' ? 'Ödeme linki' : 'Diğer'}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-[hsl(var(--text-muted))]">Gönderen</dt>
                    <dd className="font-medium">{r.sender_name}</dd>
                  </div>
                  {r.reference && (
                    <div className="flex justify-between gap-3">
                      <dt className="text-[hsl(var(--text-muted))]">Referans</dt>
                      <dd className="truncate font-mono-alt">{r.reference}</dd>
                    </div>
                  )}
                  <div className="flex justify-between gap-3">
                    <dt className="text-[hsl(var(--text-muted))]">Tarih</dt>
                    <dd className="font-mono-alt">{new Date(r.created_at).toLocaleString('tr-TR')}</dd>
                  </div>
                </dl>

                {r.note && (
                  <p className="mt-2 rounded-[8px] bg-[hsl(var(--surface-2))] px-2.5 py-2 text-xs text-[hsl(var(--text-2))]">
                    {r.note}
                  </p>
                )}

                {r.receipt_url && (
                  <a
                    href={fileUrl(r.receipt_url)}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`admin-payment-receipt-${r.id}`}
                    className="mt-3 flex items-center gap-3 rounded-[10px] border border-[hsl(var(--border-soft))] bg-[hsl(var(--surface-2))] p-2 transition-colors duration-150 hover:border-[hsl(var(--brand-red))]"
                  >
                    <img
                      src={fileUrl(r.receipt_url)}
                      alt="Dekont"
                      className="h-14 w-20 rounded-[6px] object-cover"
                    />
                    <span className="flex items-center gap-1.5 text-xs font-medium">
                      Dekontu büyüt <ExternalLink className="h-3 w-3" />
                    </span>
                  </a>
                )}

                {r.status === 'pending' ? (
                  <div className="mt-4 flex gap-2">
                    <Button
                      size="sm"
                      data-testid={`admin-payment-approve-${r.id}`}
                      onClick={() => {
                        setTarget(r);
                        setMode('approve');
                        setCredits(r.credits || 0);
                      }}
                      className="h-9 flex-1 bg-[hsl(142,70%,40%)] text-xs font-semibold text-white hover:bg-[hsl(142,70%,34%)]"
                    >
                      <Check className="mr-1.5 h-3.5 w-3.5" /> Onayla & Hak Yükle
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      data-testid={`admin-payment-reject-${r.id}`}
                      onClick={() => {
                        setTarget(r);
                        setMode('reject');
                      }}
                      className="h-9 border border-[hsl(var(--border-soft))] bg-[hsl(var(--surface-2))] text-xs hover:text-[hsl(var(--brand-red))]"
                    >
                      <X className="mr-1.5 h-3.5 w-3.5" /> Reddet
                    </Button>
                  </div>
                ) : (
                  <p className="mt-3 text-xs text-[hsl(var(--text-muted))]">
                    {r.granted_credits ? `${r.granted_credits} hak yüklendi. ` : ''}
                    {r.admin_note}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent className="border-[hsl(var(--border-soft))] bg-[hsl(var(--surface))] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">
              {mode === 'approve' ? 'Ödemeyi Onayla' : 'Bildirimi Reddet'}
            </DialogTitle>
            <DialogDescription className="text-[hsl(var(--text-muted))]">
              {target?.user_email} - {target?.package_label}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {mode === 'approve' && (
              <div className="space-y-2">
                <Label>Yüklenecek hak</Label>
                <Input
                  type="number"
                  data-testid="admin-payment-credits-input"
                  value={credits}
                  onChange={(e) => setCredits(e.target.value)}
                  className="bg-[hsl(var(--surface-2))] font-mono-alt"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label>Not (kullanıcı görebilir)</Label>
              <Input
                data-testid="admin-payment-note-input"
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
                placeholder={mode === 'approve' ? 'Teşekkürler!' : 'Ödeme bulunamadı'}
                className="bg-[hsl(var(--surface-2))]"
              />
            </div>
            <Button
              onClick={decide}
              disabled={saving}
              data-testid="admin-payment-decide-confirm"
              className={[
                'h-11 w-full font-semibold text-white',
                mode === 'approve'
                  ? 'bg-[hsl(142,70%,40%)] hover:bg-[hsl(142,70%,34%)]'
                  : 'bg-[hsl(var(--brand-red))] hover:bg-[hsl(var(--brand-red-2))]',
              ].join(' ')}
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === 'approve' ? 'Onayla' : 'Reddet'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
